import React, { useRef, useState } from "react";
import "./PinAuthScreen.scss";

const PinAuthScreen = ({
  title = "Enter PIN",
  subtitle = "Enter your 4-digit PIN to continue",
  onSubmitPin,
  onBack,
  maxLength = 4,
}) => {
  const [pin, setPin] = useState(Array(maxLength).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const inputRefs = useRef([]);

  const handleChange = (value, index) => {
    setError("");

    if (!/^\d?$/.test(value)) {
      setError("Only numbers are allowed");
      return;
    }

    const updatedPin = [...pin];
    updatedPin[index] = value;
    setPin(updatedPin);

    if (value && index < maxLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    setError("");

    const pastedValue = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, maxLength);

    if (pastedValue.length !== maxLength) {
      setError("Please paste a valid 4-digit PIN");
      return;
    }

    const updatedPin = pastedValue.split("");
    setPin(updatedPin);
    inputRefs.current[maxLength - 1]?.focus();
  };

  const handleSubmit = async () => {
    const enteredPin = pin.join("");

    if (enteredPin.length !== maxLength) {
      setError("Please enter your 4-digit PIN");
      return;
    }

    if (!/^\d{4}$/.test(enteredPin)) {
      setError("PIN must contain only 4 numbers");
      return;
    }

    try {
      setLoading(true);
      setError("");

      await onSubmitPin?.(enteredPin);
    } catch (err) {
      setError(err?.message || "Invalid PIN. Please try again.");
      setPin(Array(maxLength).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pin-auth-page">
      <div className="pin-card">
        <button
          type="button"
          className="pin-back-btn"
          onClick={onBack}
          disabled={loading}
        >
          ← Back
        </button>

        <div className="pin-icon">
          <span>🔐</span>
        </div>

        <h2>{title}</h2>
        <p>{subtitle}</p>

        <div className={`pin-input-group ${error ? "has-error" : ""}`}>
          {pin.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              className={digit ? "pin-input--filled" : ""}
              type="password"
              inputMode="numeric"
              autoComplete="off"
              aria-label={`PIN digit ${index + 1}`}
              maxLength="1"
              value={digit}
              disabled={loading}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {error && <div className="pin-error">{error}</div>}

        <button
          className="pin-submit-btn"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Verifying..." : "Continue"}
        </button>
        <button
          className="pin-clear-btn"
          disabled={loading}
          onClick={() => {
            setPin(Array(maxLength).fill(""));
            setError("");
            inputRefs.current[0]?.focus();
          }}
        >
          Clear PIN
        </button>
      </div>
    </div>
  );
};

export default PinAuthScreen;
